import { BillingGridDatasource } from '@/modules/billing-module/components/grid/config/datasources/billing-grid.datasource'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import {
    BillingAppliedBillableConceptCategory,
    billingAppliedBillableConceptService
} from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgColumnDefinition, IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { billingGridCopaymentsColumnDefs } from '@/modules/billing-module/components/grid/config/column-defs/billing-grid-copayments.column-defs'
import {
    BillingSearcherMode,
    BillingSearcherParams,
    BillingSearcherCaseMode
} from '@/modules/billing-module/components/searcher/billing-searcher.model'
import { StrictNullable } from '@/shared/types/app.types'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'
import { ReportGridRequest } from '@/api/services/common.dto'
import { AxiosResponse } from 'axios'

export enum BillingCopaymentsCategory {
    HAS_NO_CUSTOMER = 'hasNoCustomer',
    HAS_NO_VALUE = 'hasNoValue',
    HAS_VALUE = 'hasValue'
}

export class BillingGridCopaymentsDatasource extends BillingGridDatasource<AppliedBillableConceptResponse> {
    private readonly _category: BillingCopaymentsCategory | undefined

    constructor(laboratoryId: number, mode?: BillingCopaymentsCategory) {
        super(laboratoryId)
        this._category = mode
    }

    public getColumnDefs(): AgColumnDefinition[] {
        return billingGridCopaymentsColumnDefs()
    }
    protected fetchData(filterModel: CommonFilterModel, params: CommonParams): Promise<IResponseAgGrid<AppliedBillableConceptResponse>> {
        let category: BillingAppliedBillableConceptCategory | undefined
        const _filterModel: CommonFilterModel = {
            ...filterModel,
            'isCopayment': GridUtils.getSetFilterModel(['true']),
            // TODO: Replace with new blank filter when ag-grid version is updated to v27
            'deliveryNote': {
                filter: 'null',
                filterType: 'number',
                type: 'equals'
            }
        }
        switch (this._category) {
        case BillingCopaymentsCategory.HAS_NO_CUSTOMER:
            category = BillingAppliedBillableConceptCategory.HAS_NO_CUSTOMER
            break
        case BillingCopaymentsCategory.HAS_NO_VALUE:
            _filterModel['rate'] = GridUtils.getNumberFilterModel('notEqual', null)
            _filterModel['value'] = GridUtils.getNumberFilterModel('equals', 0)
            break
        case BillingCopaymentsCategory.HAS_VALUE:
            _filterModel['rate'] = GridUtils.getNumberFilterModel('notEqual', null)
            _filterModel['value'] = GridUtils.getNumberFilterModel('notEqual', 0)
            break
        }
        return billingAppliedBillableConceptService.paginator(_filterModel, params, this.laboratoryId, category)
    }

    public exporter(request: ReportGridRequest, params: ExportCommonParams): Promise<AxiosResponse<Blob>> {
        let category: BillingAppliedBillableConceptCategory | undefined
        const filterModel: CommonFilterModel = {
            ...request.filterModel,
            'isCopayment': GridUtils.getSetFilterModel(['true'])
        }
        switch (this._category) {
        case BillingCopaymentsCategory.HAS_NO_CUSTOMER:
            category = BillingAppliedBillableConceptCategory.HAS_NO_CUSTOMER
            break
        case BillingCopaymentsCategory.HAS_NO_VALUE:
            filterModel['rate'] = GridUtils.getNumberFilterModel('notEqual', null)
            filterModel['value'] = GridUtils.getNumberFilterModel('equals', 0)
            break
        case BillingCopaymentsCategory.HAS_VALUE:
            filterModel['rate'] = GridUtils.getNumberFilterModel('notEqual', null)
            filterModel['value'] = GridUtils.getNumberFilterModel('notEqual', 0)
            break
        }
        return billingAppliedBillableConceptService.export(this.laboratoryId, { ...request, filterModel }, params, category)
    }

    public getSearcherModes(): BillingSearcherMode[] {
        return [
            BillingSearcherMode.PATIENT,
            BillingSearcherMode.CASE
        ]
    }
    public searcher(searcherParams: BillingSearcherParams): Promise<StrictNullable<IResponseAgGrid<AppliedBillableConceptResponse>>> {
        const { mode, caseScope, patientsIds, identifierSearch } = searcherParams

        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel: CommonFilterModel = {}
        if (mode === BillingSearcherMode.PATIENT && patientsIds) {
            filterModel['caseStudy.patient.id'] = GridUtils.getSetFilterModel(patientsIds.map(patientId => `${patientId}`))
        } else if (mode === BillingSearcherMode.CASE) {
            let caseFieldName: string | undefined
            if (caseScope === BillingSearcherCaseMode.IDENTIFIER) {
                caseFieldName = 'caseStudy.casCasenumber'
            } else if (caseScope === BillingSearcherCaseMode.INSURANCE_DOCUMENT) {
                caseFieldName = 'caseStudy.referralNumber'
            } else if (caseScope === BillingSearcherCaseMode.INSURANCE_POLICY) {
                caseFieldName = 'caseStudy.insuranceNumber'
            }
            if (caseFieldName) {
                filterModel[caseFieldName] = GridUtils.getTextFilterModel('contains', identifierSearch)
            }
        }
        return this.fetchData(filterModel, params)
    }
}
